import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import useNotifications from "@/composables/useNotifications";
import i18n from '@/libs/i18n';
import useCommon from '@/views/organization/useCommon'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import endpoints from '@/libs/endpoints'
import useCommonTodo from '@/views/apps/todo/useCommonTodo'

export default function useBarometerSurveyDesignedList() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { handleError, getLocationsForDropDown, locations,  getPhysicalLocationsForDropDown, physical_locations } = useCommon()
  const { updateItem, getItemsAggregate, createItem, getItems, sendEmail } = useLambda()
  const { getEmailBarometerSurveyTemplate } = useCommonTodo()
  const userData = store.state?.userStore?.userData
  const clientId = userData.role !== "admin" ? userData.client.$oid : null
  const barometerSurveysDesigned = ref([])
  const collection = 'barometer_survey_designed'
  // Table Headers
  const tableColumns = computed(() => ([
    { key: 'name', label: i18n.t('message.tableHeader.name'), sortable: true },
    { key: 'Actions', label: i18n.t('message.tableHeader.actions') },
  ]))

  const totalBarometerSurveysDesigned = ref(0)
  const perPage = ref(10)
  const currentPage = ref(1)
  const perPageOptions = [5, 10, 15, 20, 25, 30, 40, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('name')
  const isSortDirDesc = ref(false)
  const isLoading = ref(true)

  const fetchBarometerSurveysDesigned = async (searchValue = '') => {
    isLoading.value = true

    try {
      const query = {
        client_id: oid(clientId),
        deleted: { $ne: true },
      }

      const pipeline = [
        { $match: query },
      ]

      const items = await getItemsAggregate({ collection, pipeline })
      if (!items) return
  
      // Search
      if (searchValue && searchValue !== searchQuery.value) return

      barometerSurveysDesigned.value  = searchQuery.value ? items.filter(e => e.name?.toLowerCase().includes(searchQuery.value.toLowerCase())) : items
      totalBarometerSurveysDesigned.value = barometerSurveysDesigned.value.length
    
    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: i18n.t('message.barometer_survey_list_fetch_error') })
    } finally {
      isLoading.value = false
    }
  }

  const searchBarometerSurveyDesigned = searchValue => {
    isLoading.value = true
    fetchBarometerSurveysDesigned(searchValue)
  }

  const deleteBarometerSurveyDesigned = async (id) => {
    try {
      const query = { _id: id }
      const action = { $set: { deleted: true } }

      await updateItem({ collection, query, action })

      fetchBarometerSurveysDesigned()
      showSuccessMessage(i18n.t('message.barometer_survey_remove'))
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.something_went_wrong'))
    }
  }

  const showingMessage = computed(() => {
    const from = ((currentPage.value * perPage.value) - perPage.value) + (barometerSurveysDesigned.value.length ? 1 : 0)
    const to = barometerSurveysDesigned.value.length + ((currentPage.value * perPage.value) - perPage.value)

    return i18n.tc('message.paginationText', 0, { from: from, to: to, total: totalBarometerSurveysDesigned.value })
  })

  watch([currentPage, perPage], () => {
    fetchBarometerSurveysDesigned()
  })

  watch([searchQuery, sortBy, isSortDirDesc], () => {
    currentPage.value = 1
    fetchBarometerSurveysDesigned()
  })

  // New Survey Modal

  const newSurvey = ref({ name: '', location: null, physical_location: '', link: '' })
  const modalValidation = ref(null)
  const isCreatingSurvey = ref(false)
  const isSurveyCreated = ref(false)
  const workers = ref([])
  const showMailModal = ref(false)
  const mailData = ref({})

  const handleCancel = () => {
    newSurvey.value = { name: '', location: null, physical_location: '', link: '' }
    isSurveyCreated.value = false
    showMailModal.value = false
    mailData.value = {}
  }

  const createSurvey = async (event, surveyDesignedData) => {
    event.preventDefault()

    const isModalValidated = await modalValidation.value.validate()

    if (!isModalValidated) {
      showErrorMessage(i18n.t('message.requiredFieldsIncomplete'))
      return
    }

    try {
      isCreatingSurvey.value = true

      const payload = {
        client_id: oid(clientId),
        name: newSurvey.value.name,
        location: oid(newSurvey.value.location),
        physical_location: oid(newSurvey.value.physical_location),
        surveyDesigned: surveyDesignedData._id,
        questions: surveyDesignedData.questions,
        comment: surveyDesignedData.comment,
      }

      const { insertedId } = await createItem({ collection: 'barometer_survey_created', payload })
      
      showSuccessMessage(i18n.t('message.survey_added'))

      newSurvey.value.link = `${endpoints.app_url}/barometer-survey/${insertedId.toString()}`
      isSurveyCreated.value = true

      // Update survey with link to created survey
      const query = { _id: oid(insertedId) }
      const action = { $set: { link: newSurvey.value.link } }

      updateItem({ collection: 'barometer_survey_created', query, action })
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.survey_error'))
    } finally {
      isCreatingSurvey.value = false
    }
  }

  const copyLink = (link) => {
    navigator.clipboard.writeText(link)
      .then(() => showSuccessMessage(i18n.t('message.linkCopiedToClipboard')))
      .catch(() => showErrorMessage(i18n.t('message.copyLinkError')))
  }

  const confirmSendEmail = () => {
    if (!workers.value?.length || !newSurvey.value.link) return

    // Find department name
    const selectedLocation = locations.value?.find(loc => loc.value === newSurvey.value.location)
    const locationName = selectedLocation?.title || ''

    // Filter workers in the selected department who have an email
    const workersWithLocation = workers.value?.filter(worker => worker?.locations?.some(locId => locId?.toString() === newSurvey.value?.location)) || []
    const workersWithEmail = workersWithLocation?.filter(w => !!w?.email) || []

    // Build the data for sending emails
    mailData.value = Object.assign(
      workersWithEmail?.map(({ name, email }) => ({ name: name || '', email: email || '' })) || [],
      {
        totalWithEmail: workersWithEmail?.length || 0,
        totalWithoutEmail: (workersWithLocation?.length || 0) - (workersWithEmail?.length || 0),
        locationName,
        surveyName: newSurvey.value?.name || '',
        link: newSurvey.value?.link || '',
      }
    )
    showMailModal.value = true
  }

  const handleSendMail = () => {
    if (!workers.value?.length || !newSurvey.value?.link) return

    mailData.value.forEach(worker => {
      if (!worker.email) return

      const subject = `${i18n.t('message.Survey')}: ${mailData.value.surveyName}`
      const body = getEmailBarometerSurveyTemplate({
        name: worker.name,
        surveyName: mailData.value.surveyName,
        locationName: mailData.value.locationName,
        link: mailData.value.link
      });

      sendEmail({ to: [worker.email], subject, htmlBody: body })
        .then((response) => {
          if (response.MessageId) console.log(`Correo enviado a ${worker.email}`)
          showSuccessMessage(i18n.t('message.survey_success'))
        })
        .catch((err) => {
          console.log(err);
           showErrorMessage(i18n.t('message.survey_submit_error'))
        })
    })
    showMailModal.value = false
  }

  const getEmailsWorkers = async () => {
    const query = {
      client_id: oid(clientId),
      deleted: { $ne: true },
      privileges: 'employee'
    }
    try {
      const items = await getItems({ collection: 'worker', query })
      if (items.length) {
        workers.value = items.map(({ _id, email, name, locations }) => ({
          _id,
          email,
          name,
          locations
        }))
      }
    } catch (error) {
      console.log(error)
    }
  }

  return {
    fetchBarometerSurveysDesigned,
    tableColumns,
    perPage,
    currentPage,
    perPageOptions,
    totalBarometerSurveysDesigned,
    showingMessage,
    searchQuery,
    sortBy,
    isSortDirDesc,
    barometerSurveysDesigned,
    deleteBarometerSurveyDesigned,
    isLoading,
    searchBarometerSurveyDesigned,
    newSurvey,
    modalValidation,
    isCreatingSurvey,
    createSurvey,
    getLocationsForDropDown,
    getPhysicalLocationsForDropDown,
    locations,
    physical_locations,
    isSurveyCreated,
    copyLink,
    handleSendMail,
    getEmailsWorkers,
    showMailModal,
    confirmSendEmail,
    mailData,
    handleCancel
  }
}